import { Link } from 'react-router';
import { ArrowLeft } from 'lucide-react';
import { PublicFooter } from '@/components/PublicFooter';
import { usePageMeta } from '@/hooks/usePageMeta';

export default function Privacy() {
  usePageMeta({
    title: 'Privacy Policy',
    description:
      'For Visibility Privacy Policy — how we handle your marketplace, financial, and personal data. We never sell your data.',
    canonicalPath: '/legal/privacy',
    ogPage: 'privacy',
  });
  return (
    <div className="min-h-screen bg-fos-bg-base">
      <div className="mx-auto max-w-3xl px-4 py-12">
        <Link
          to="/"
          className="mb-8 inline-flex items-center gap-1 text-sm text-fos-text-muted transition-colors hover:text-fos-text-primary"
        >
          <ArrowLeft className="h-4 w-4" /> Back to home
        </Link>

        <article className="prose prose-gray max-w-none">
          <h1>Privacy Policy</h1>
          <p className="lead">Last updated: May 12, 2026</p>

          <p>
            At For Visibility, your privacy is fundamental to how we build our product. This policy
            explains what data we collect, how we use it, and your rights over your information.
          </p>

          <h2>1. Data We Collect</h2>

          <h3>1.1 Information You Provide</h3>
          <ul>
            <li>
              <strong>Account Data:</strong> Name, email address, and password when you create an
              account.
            </li>
            <li>
              <strong>Profile Data:</strong> Optional information such as profile photo, business
              name, and preferences.
            </li>
            <li>
              <strong>Marketplace Connections:</strong> Authorization tokens for connected selling
              platforms (eBay, Mercari, Poshmark). We never store your marketplace login
              credentials.
            </li>
            <li>
              <strong>Content:</strong> Listings, product descriptions, pricing data, images, and
              inventory records you create or import through the Service.
            </li>
            <li>
              <strong>Communications:</strong> Information you provide when contacting support or
              participating in community channels.
            </li>
          </ul>

          <h3>1.2 Information Collected Automatically</h3>
          <ul>
            <li>
              <strong>Usage Data:</strong> Pages visited, features used, session duration, and
              interaction patterns.
            </li>
            <li>
              <strong>Device & Browser Data:</strong> IP address, browser type and version,
              operating system, and device identifiers.
            </li>
            <li>
              <strong>Performance Data:</strong> Page load times, error reports, and API response
              metrics used to improve service reliability.
            </li>
          </ul>

          <h2>2. How We Use Your Data</h2>
          <p>We use your data to:</p>
          <ul>
            <li>Provide, maintain, and improve the Service</li>
            <li>Process marketplace connections and sync operations</li>
            <li>Train and improve our AI models (using aggregated, de-identified data)</li>
            <li>Send service-related communications (billing, security, product updates)</li>
            <li>Detect and prevent abuse, fraud, or policy violations</li>
            <li>Comply with legal obligations</li>
          </ul>

          <h2>3. Data Sharing</h2>
          <p>We share your data only in these limited circumstances:</p>
          <ul>
            <li>
              <strong>Marketplace APIs:</strong> When you connect a marketplace, data is shared with
              that platform's API as necessary to provide the integration.
            </li>
            <li>
              <strong>Service Providers:</strong> We use trusted third parties for hosting (Vercel),
              analytics, and payment processing. These providers are contractually bound to protect
              your data.
            </li>
            <li>
              <strong>Legal Requirements:</strong> If required by law, court order, or valid
              governmental request, we may disclose your information.
            </li>
          </ul>
          <p>
            <strong>We do not sell your personal information.</strong> We do not share your data
            with third parties for their marketing purposes. Period.
          </p>

          <h2>4. Data Retention</h2>
          <p>
            We retain your data for as long as your account is active. If you cancel your
            subscription, your data remains available for export for 30 days, after which it is
            permanently deleted from our production systems. Backup archives are retained for up to
            90 additional days before complete purging.
          </p>

          <h2>5. Data Security</h2>
          <p>
            We implement industry-standard security measures including encryption at rest (AES-256),
            encryption in transit (TLS 1.3), regular security audits, and strict access controls.
            See our{' '}
            <Link to="/legal/security" className="text-fos-accent hover:underline">
              Security page
            </Link>{' '}
            for detailed information.
          </p>

          <h2>6. Your Rights</h2>
          <p>Depending on your jurisdiction, you may have the right to:</p>
          <ul>
            <li>Access the personal data we hold about you</li>
            <li>Correct inaccurate or incomplete data</li>
            <li>Delete your data (subject to legal retention requirements)</li>
            <li>Export your data in a portable format (CSV, JSON)</li>
            <li>Object to or restrict certain processing activities</li>
            <li>Withdraw consent where processing is based on consent</li>
          </ul>
          <p>
            To exercise these rights, contact us at{' '}
            <a href="mailto:privacy@forvisibility.com">privacy@forvisibility.com</a>. We respond to
            all requests within 30 days.
          </p>

          <h2>7. Cookies</h2>
          <p>
            We use essential cookies for authentication and session management, and analytics
            cookies to understand how the Service is used. You can control cookie preferences
            through your browser settings. Blocking essential cookies may prevent the Service from
            functioning correctly.
          </p>

          <h2>8. International Data Transfers</h2>
          <p>
            Our infrastructure is hosted with Vercel in the United States. If you access the Service
            from outside the U.S., your data may be transferred to, stored in, and processed in the
            United States. We rely on Standard Contractual Clauses and other legal mechanisms to
            ensure adequate protection for international data transfers.
          </p>

          <h2>9. Children's Privacy</h2>
          <p>
            The Service is not intended for individuals under 18 years of age. We do not knowingly
            collect personal information from children. If we become aware that a child has provided
            us with personal data, we will delete it promptly.
          </p>

          <h2>10. Policy Updates</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of material
            changes via email and through the Service. We encourage you to review this policy
            periodically. Continued use of the Service after changes take effect constitutes
            acceptance of the updated policy.
          </p>

          <h2>11. Contact</h2>
          <p>For privacy-related inquiries or to exercise your data rights:</p>
          <ul>
            <li>
              Email: <a href="mailto:privacy@forvisibility.com">privacy@forvisibility.com</a>
            </li>
            <li>
              Mail: For Visibility Inc., Data Protection Officer, 123 Commerce Street, Wilmington,
              DE 19801
            </li>
          </ul>
        </article>
      </div>
      <PublicFooter />
    </div>
  );
}
